/**
 * Auth storage utility
 * 
 * Refresh tokens are now stored in httpOnly cookies (set by the server),
 * so they cannot be accessed from JavaScript - this protects against XSS attacks.
 * 
 * Only the username is stored in localStorage for UI purposes.
 */

import {
  DEFAULT_ADMIN_PERMISSIONS,
  normalizeAdminPermissions,
  type AdminPermissions,
} from '@swtorbit/shared-socket-events';

const USERNAME_KEY = 'admin_username';
const SUPER_ADMIN_KEY = 'admin_is_super';
const PERMISSIONS_KEY = 'admin_permissions';

export const authStorage = {
  setUsername(username: string): void {
    localStorage.setItem(USERNAME_KEY, username);
  },

  getUsername(): string | null {
    return localStorage.getItem(USERNAME_KEY);
  },

  /** Cache the super-admin flag so the panel can render before the first refresh completes. */
  setSuperAdmin(isSuperAdmin: boolean): void {
    localStorage.setItem(SUPER_ADMIN_KEY, isSuperAdmin ? '1' : '0');
  },

  getSuperAdmin(): boolean {
    return localStorage.getItem(SUPER_ADMIN_KEY) === '1';
  },

  /** Cache the admin's own permissions so UIs (e.g. game-type picker) render correctly before the first refresh. */
  setPermissions(permissions: AdminPermissions): void {
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions));
  },

  getPermissions(): AdminPermissions {
    const raw = localStorage.getItem(PERMISSIONS_KEY);
    if (!raw) return DEFAULT_ADMIN_PERMISSIONS;
    return normalizeAdminPermissions(raw);
  },

  clearAuth(): void {
    localStorage.removeItem(USERNAME_KEY);
    localStorage.removeItem(SUPER_ADMIN_KEY);
    localStorage.removeItem(PERMISSIONS_KEY);
  },

  hasStoredSession(): boolean {
    // We can't check for refresh token (it's httpOnly), but username indicates a previous session
    return !!this.getUsername();
  },
};
